import { createSelector } from 'reselect';
import { getMin, getMax, getHourIntervals, startOfHour, minutesElapsed, isAfter } from '../../lib/date.utils';
import { find, filter, forEach, sortBy, map, flatten, startsWith, get, uniq } from 'lodash';
import { filterSelectedPartnerSelector, stagesFilterSelector, liveViewSelector } from './filter';
import { notificationsSeenSelector } from '../authentication';
import { LIVE_VIEW_TYPE, POST_VIEW_TYPE } from '../../constants/clearance';

const timetable = (state) => state.events.timetable;
const notifications = (state) => state.events.timetable.notifications;

// TIMETABLE SELECTORS
export const daysSelector = createSelector(timetable, (tt) => tt.days);
export const nowSelector = createSelector(timetable, (tt) => tt.now);
export const allStagesSelector = createSelector(timetable, (tt) => tt.stages);
export const selectedDaySelector = createSelector(timetable, (tt) => tt.selectedDay);
export const zoomSelector = createSelector(timetable, (tt) => tt.zoom);

export const chatEnabledSelector = createSelector(timetable, (tt) => {
    return tt.chatEnabled;
});
export const notificationsSelector = createSelector(notifications, (n) => n || {});
export const showDotNotificationsSelector = createSelector(
    notificationsSelector,
    notificationsSeenSelector,
    (n, ns) => {
        const lastNotification = get(n, 'data[0]');
        return lastNotification && isAfter(lastNotification.createdAt, ns);
    }
);
export const stagesSelector = createSelector(timetable, stagesFilterSelector, (tt, stf) => {
    return filter(
        sortBy(
            tt.stages.filter((st) => st.date === tt.selectedDay),
            (s) => parseInt(s.number, 10)
        ),
        (x) => (stf.length ? stf.includes(x.name) : true)
    );
});

export const listViewEntriesSelector = createSelector(timetable, filterSelectedPartnerSelector, (tt, partnerId) => {
    return flatten(
        tt.stages.map((t) =>
            (t.Performances || []).map((p) => ({
                start: p.startHour,
                end: p.endHour,
                artistName: (p.Artist && p.Artist.name) || '',
                artistId: (p.Artist && p.Artist.id) || '',
                id: p.id,
                clearing: find(p.clearing, (c) => c.partnerId === partnerId),
                stageName: t.name,
                stageDate: t.date
            }))
        )
    );
});

export const listViewArtistsSelector = createSelector(listViewEntriesSelector, (entries) => {
    return map(uniq(sortBy(entries.map((x) => x.artistName))), (a) => ({ value: a, label: a }));
});

export const listViewStagesSelector = createSelector(listViewEntriesSelector, (entries) => {
    return map(uniq(sortBy(entries.map((x) => x.stageName))), (s) => ({ value: s, label: s }));
});

export const visibleCategoriesSelector = createSelector(
    listViewEntriesSelector,
    liveViewSelector,
    (entries, isLiveView) => {
        const viewType = isLiveView ? LIVE_VIEW_TYPE : POST_VIEW_TYPE;
        const categoryMap = {};

        entries.forEach((entry) => {
            if (!entry.clearing || typeof entry.clearing !== 'object') return;
            Object.entries(entry.clearing).forEach(([categoryId, value]) => {
                if (value?.category?.label && value?.category?.visibleInViews?.includes(viewType)) {
                    if (!categoryMap[categoryId]) {
                        categoryMap[categoryId] = {
                            id: categoryId,
                            label: value.category.label,
                            labels: new Set()
                        };
                    }
                    if (value.label?.label && value.label.label.toLowerCase() !== 'hidden') {
                        categoryMap[categoryId].labels.add(value.label.label);
                    }
                }
            });
        });

        return Object.values(categoryMap).map((category) => ({
            id: category.id,
            label: category.label,
            options: Array.from(category.labels)
                .sort()
                .map((label) => ({ value: label, label }))
        }));
    }
);

export const hoursSelector = createSelector(stagesSelector, (stages) => {
    return _hoursParser(stages);
});
export const entrySelector = createSelector(timetable, (tt) => (tt.entry.id ? tt.entry : null));
export const contactSelector = createSelector(timetable, (tt) => tt.entry.contact);
export const entryArtistSelector = createSelector(timetable, (tt) => tt.entry.artistId);
export const fetchingSelector = createSelector(timetable, (tt) => tt.fetching);

export const firstNowSelector = createSelector(timetable, (tt) => {
    const firstNow = find(sortBy(tt.stages, ['date']), (stage) => {
        const hoursParsed = _hoursParser([stage]);
        const elapsed = minutesElapsed(hoursParsed.min, hoursParsed.max, tt.now);
        return elapsed > 0;
    });
    return firstNow;
});

export const showNow = createSelector(firstNowSelector, (nowStage) => {
    return !!nowStage;
});

const _hoursParser = (stages) => {
    const dates = [];
    stages.forEach((s) => {
        s.Performances.forEach((e) => {
            dates.push(e.startHour, e.endHour);
        });
    });
    const min = startOfHour(getMin(dates));
    const max = startOfHour(getMax(dates));
    const intervals = getHourIntervals(min, max);
    return { intervals, min, max };
};
// DATA SELECTORS
export const getClearingForPartner = (entry, partner) => {
    if (get(entry, 'clearing.partnerId') === partner) {
        return entry.clearing;
    }
    return find(entry.clearing || [], (c) => c.partnerId === partner) || {};
};

export const showState = (state) => state && state !== 'hidden';

/**
 * Determine if an entry should be displayed based on applied filters
 * @param {string} artistName - Name of the artist
 * @param {Object} clearing - Clearing data for the entry (category-based structure)
 * @param {Array} artistsFilters - Selected artist names to filter by
 * @param {Object} categoryFilters - Object with categoryId keys and arrays of selected labels as values
 * @param {boolean} isLiveView - Whether we're in live view mode
 * @returns {boolean} Whether the entry should be displayed
 */
export const displayEntry = (artistName, clearing, artistsFilters, categoryFilters = {}, isLiveView = true) => {
    let display = true;

    if (artistsFilters.length) {
        display = artistsFilters.includes(artistName);
    }

    // Filter by category labels
    const viewType = isLiveView ? LIVE_VIEW_TYPE : POST_VIEW_TYPE;
    Object.entries(categoryFilters).forEach(([categoryId, selectedLabels]) => {
        if (selectedLabels && selectedLabels.length > 0) {
            const categoryValue = clearing?.[categoryId];
            const isVisibleInView = categoryValue?.category?.visibleInViews?.includes(viewType);
            const entryLabel = categoryValue?.label?.label || '';

            // Only apply filter if category is visible in current view
            if (isVisibleInView) {
                display = display && selectedLabels.includes(entryLabel);
            }
        }
    });

    return display;
};

export const nowOffset = (widthUnit, minHour, maxHour, now) => (widthUnit / 60) * minutesElapsed(minHour, maxHour, now);

export function stagesToState(stages) {
    const parsedStages = [];
    stages.forEach((s) => {
        forEach(s.names, (name, date) => {
            parsedStages.push({
                ...s,
                name,
                date,
                Performances: s.Performances.filter((x) => startsWith(x.date, date))
            });
        });
    });
    return parsedStages;
}
